<template>
  <b-card no-body class="ag-card" :class="accentClass">
    <button type="button" class="ag-header" :aria-expanded="isOpen.toString()" @click="toggle">
      <span class="ag-header-left">
        <span class="ag-title">Request Evaluation</span>
        <span v-if="recommendedLabel" class="ag-badge" :class="badgeClass">{{ recommendedLabel }}</span>
      </span>
      <span class="ag-chevron" :class="{ 'is-open': isOpen }">
        <i class="fa-solid fa-chevron-down"></i>
      </span>
    </button>

   <b-collapse v-model="isOpen">
      <div class="ag-body">

        <div v-if="hasEvaluation" class="ag-response-eval-row">
          <button type="button" class="ag-btn-primary ag-response-eval-btn" @click="scrollToResponses">
            Response Evaluations <i class="fa-solid fa-chevron-down ml-2"></i>
          </button>
        </div>

        <p v-if="!isEvaluating && !errorMessage && evaluatedAtLabel" class="ag-timestamp">
          <i class="fa-regular fa-clock mr-1"></i>Last evaluated {{ evaluatedAtLabel }}
        </p>
        <!-- LOADING -->
        <div v-if="isEvaluating" class="ag-loading">
          <b-spinner small variant="light" />
          <p class="ag-muted mt-2 mb-0">Analyzing...</p>
        </div>

        <!-- ERROR -->
        <div v-else-if="errorMessage" class="ag-empty">
          <p class="mb-3">{{ errorMessage }}</p>
          <button type="button" class="ag-btn-primary" @click="generate">Try again</button>
        </div>

        <!-- EMPTY: cannot evaluate (no text) -->
        <div v-else-if="!hasEvaluation && !canGenerate" class="ag-empty">
          <p class="mb-0">No text to evaluate.</p>
        </div>

        <!-- EMPTY: evaluation not generated -->
        <div v-else-if="!hasEvaluation" class="ag-empty">
          <p class="mb-3">No evaluation generated yet for this.</p>
          <button type="button" class="ag-btn-primary" @click="generate">
            <i class="fa-solid fa-wand-magic-sparkles mr-2"></i>Generate evaluation
          </button>
        </div>

        <!-- CONTENT -->
        <div v-else>
          <p class="ag-summary">{{ summaryText }}</p>

          <hr class="ag-divider" />

          <!-- GOOD INTENTS — only shown when score is Approve -->
          <div v-if="detailsBlock" class="ag-section">
            <h5 class="ag-section-title">Good Intents</h5>
            <hr class="ag-section-divider" />
            <div class="ag-chips-row">
              <span v-for="(flag, idx) in detailsBlock.flags" :key="`f-${idx}`" class="ag-chip"
                :class="flag.value === 'Yes' ? 'ag-chip-safe' : 'ag-chip-danger'">
                <i :class="flag.value === 'Yes' ? 'fa-solid fa-check' : 'fa-solid fa-times'"></i>
                {{ flag.label }}
              </span>
            </div>
            <p v-for="(line, idx) in detailsBlock.paragraphs" :key="`d-${idx}`" class="ag-paragraph">{{ line }}</p>
          </div>

          <!-- SENTIMENT -->
          <div v-if="sentimentBlock" class="ag-section">
            <h5 class="ag-section-title">Sentiment</h5>
            <hr class="ag-section-divider" />
            <div v-if="sentimentBlock.values" class="ag-chips-row">
              <span v-for="val in sentimentBlock.values.split(', ')" :key="val" class="ag-chip" :class="{
                'ag-chip-danger': ['demanding', 'impatient', 'angry', 'hostile', 'frustrated'].includes(val.toLowerCase()),
                'ag-chip-warning': ['assertive', 'skeptical', 'cautious', 'uncertain'].includes(val.toLowerCase()),
                'ag-chip-safe': ['hopeful', 'inquisitive', 'grateful', 'enthusiastic', 'open'].includes(val.toLowerCase())
              }">
                {{ val }}
              </span>
            </div>
            <p v-for="(line, idx) in sentimentBlock.paragraphs" :key="`s-${idx}`" class="ag-paragraph">{{ line }}</p>
          </div>

          <!-- HUMANITY -->
          <div v-if="humanityBlock" class="ag-section">
            <h5 class="ag-section-title">Humanity Check</h5>
            <hr class="ag-section-divider" />
            <div v-if="humanityBlock.flag" class="ag-chips-row">
              <span class="ag-chip"
                :class="['yes', 'likely'].includes(humanityBlock.flag.value.toLowerCase()) ? 'ag-chip-safe' : 'ag-chip-warning'">
                <i
                  :class="['yes', 'likely'].includes(humanityBlock.flag.value.toLowerCase()) ? 'fa-solid fa-check' : 'fa-solid fa-question'"></i>
                Human Authorship: {{ humanityBlock.flag.value }}
              </span>
            </div>
            <p v-for="(line, idx) in humanityBlock.paragraphs" :key="`h-${idx}`" class="ag-paragraph">{{ line }}</p>
          </div>

          <!-- REALISM -->
          <div v-if="realismBlock" class="ag-section">
            <h5 class="ag-section-title">Realism Check</h5>
            <hr class="ag-section-divider" />
            <div v-if="realismBlock.flag" class="ag-chips-row">
              <span class="ag-chip" :class="{
                'ag-chip-safe': realismBlock.flag.value === 'Likely',
                'ag-chip-warning': realismBlock.flag.value === 'Uncertain',
                'ag-chip-danger': realismBlock.flag.value === 'Unlikely'
              }">
                <i :class="{
                  'fa-solid fa-check': realismBlock.flag.value === 'Likely',
                  'fa-solid fa-question': realismBlock.flag.value === 'Uncertain',
                  'fa-solid fa-times': realismBlock.flag.value === 'Unlikely'
                }"></i>
                {{ realismBlock.flag.value }}
              </span>
            </div>
            <p v-for="(line, idx) in realismBlock.paragraphs" :key="`r-${idx}`" class="ag-paragraph">{{ line }}</p>
          </div>
        </div>
      </div>
    </b-collapse>
  </b-card>
</template>

<script>
import axios from 'axios';
import { interpretEvaluation, severityToLabel } from '@/shared/evaluationUtils';

export default {
  name: 'ApprovalGuidance',
  props: {
    requestId: { type: String, required: true },
    requestText: { type: String, default: '' },
    evaluation: { type: [Object, null], default: null },
    autoOpen: { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: this.autoOpen,
      isEvaluating: false,
      errorMessage: '',
      localRequestEval: null,
    };
  },
  watch: {
    evaluation: {
      immediate: true,
      handler(val) {
        if (val && !val._evaluatedAt) {
          this.localRequestEval = { ...val, _evaluatedAt: new Date().toISOString() };
        }
      }
    }
  },
  computed: {
    canGenerate() {
      return !!this.requestText;
    },
    activeEvaluation() {
      return this.localRequestEval || this.evaluation;
    },
    hasEvaluation() {
      const e = this.activeEvaluation;
      if (!e || e.error) return false;
      return !!(e.summary || e.all_results || e.recommended || e.overall_explanation);
    },
    severity() {
      return interpretEvaluation(this.activeEvaluation);
    },
    // Single source of truth for the badge label
    recommendedLabel() {
      if (this.severity === 'neutral') return this.hasEvaluation ? '' : 'NO EVALUATION YET';
      return severityToLabel(this.severity);
    },
    // Raw score string from the evaluation (e.g. 'Approve', 'Do Not Approve')
    recommendedScore() {
      const e = this.activeEvaluation;
      if (!e || !e.recommended) return null;
      return typeof e.recommended.score === 'string' ? e.recommended.score : null;
    },
    accentClass() {
      return `ag-accent-${this.severity === 'neutral' ? 'neutral' : this.severity}`;
    },
    badgeClass() {
      return `ag-badge-${this.severity === 'safe' ? 'safe' : this.severity === 'neutral' ? 'neutral' : this.severity}`;
    },
    summaryText() {
      const e = this.activeEvaluation;
      if (!e || !e.summary) return '';
      if (typeof e.summary === 'object') return e.summary.summary_of_results || '';
      if (typeof e.summary === 'string') return e.summary;
      return '';
    },
    parsedResults() {
      const e = this.activeEvaluation;
      if (!e || !Array.isArray(e.all_results)) return {};
      const out = {};
      e.all_results.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        Object.keys(item).forEach((k) => { if (!out[k]) out[k] = item[k]; });
      });
      return out;
    },
    detailsBlock() {
      // Good Intents only shown when score is 'Approve'
      if (this.recommendedScore !== 'Approve') return null;
      const r = this.parsedResults;
      const gi = r.good_intents;
      const tagging = r.tagging;
      const flags = [];
      const paragraphs = [];
      const tags = [];
      if (gi) {
        if (gi.non_marketing_email !== undefined) flags.push({ label: 'Genuine email', value: this.formatYesNo(gi.non_marketing_email) });
        if (gi.non_phishing_email !== undefined) flags.push({ label: 'Safe email', value: this.formatYesNo(gi.non_phishing_email) });
        if (gi.marketing_explanation) paragraphs.push(gi.marketing_explanation);
        if (gi.phishing_explanation) paragraphs.push(gi.phishing_explanation);
      }
      if (tagging && tagging.contextual_tags) {
        const ct = tagging.contextual_tags;
        if (Array.isArray(ct)) tags.push(...ct);
        else if (typeof ct === 'string' && ct !== 'N/A') tags.push(...ct.split(',').map((t) => t.trim()).filter(Boolean));
      }
      if (!flags.length && !paragraphs.length && !tags.length) return null;
      return { flags, paragraphs, tags };
    },
    sentimentBlock() {
      const s = this.parsedResults.sentiment;
      if (!s) {
        const e = this.activeEvaluation;
        if (e && typeof e.sentiment === 'string') return { values: '', paragraphs: [e.sentiment] };
        return null;
      }
      const valuesArr = Array.isArray(s.sentiment_values) ? s.sentiment_values : [];
      const explanation = Array.isArray(s.explanation) ? s.explanation : (s.explanation ? [s.explanation] : []);
      const values = valuesArr.length ? valuesArr.join(', ') : '';
      if (!values && !explanation.length) return null;
      return { values, paragraphs: explanation };
    },
    humanityBlock() {
      const h = this.parsedResults.humanity;
      if (!h) return null;
      const flag = h.human_authorship !== undefined
        ? { label: 'Human Authorship', value: this.formatYesNo(h.human_authorship) }
        : null;
      const paragraphs = h.explanation ? (Array.isArray(h.explanation) ? h.explanation : [h.explanation]) : [];
      if (!flag && !paragraphs.length) return null;
      return { flag, paragraphs };
    },
    realismBlock() {
      const r = this.parsedResults.realism;
      if (!r) return null;
      const flag = r.request_realism !== undefined ? { label: 'Realism', value: r.request_realism }
        : (r.realistic !== undefined ? { label: 'Realistic', value: this.formatYesNo(r.realistic) }
          : (r.score !== undefined ? { label: 'Score', value: String(r.score) } : null));
      const paragraphs = r.explanation ? (Array.isArray(r.explanation) ? r.explanation : [r.explanation]) : [];
      if (!flag && !paragraphs.length) return null;
      return { flag, paragraphs };
    },
    evaluatedAtLabel() {
      const e = this.activeEvaluation;
      const raw = e && e._evaluatedAt;
      if (!raw) return '';
      const d = new Date(raw);
      if (isNaN(d.getTime())) return '';
      const diffMs = Date.now() - d.getTime();
      const min = Math.round(diffMs / 60000);
      if (min < 1) return 'just now';
      if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
      const hr = Math.round(min / 60);
      if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
      const days = Math.round(hr / 24);
      if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
  },
  methods: {
    scrollToResponses() {
      const el = document.getElementById('responses-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    toggle() { this.isOpen = !this.isOpen; },
    open() { this.isOpen = true; },
    formatYesNo(value) {
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'string') {
        const v = value.toLowerCase();
        if (v === 'yes' || v === 'true') return 'Yes';
        if (v === 'no' || v === 'false') return 'No';
        return value;
      }
      return String(value);
    },
    async generate() {
      if (!this.canGenerate) {
        this.errorMessage = 'No request text available to evaluate.';
        return;
      }
      this.isEvaluating = true;
      this.errorMessage = '';
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/asks/${this.requestId}/get-athena-analysis`,
          { headers: { 'x-access-token': token } }
        );
        this.localRequestEval = { ...data, _evaluatedAt: new Date().toISOString() };
        this.$emit('evaluation-updated', data);
      } catch (err) {
        console.error('Approval guidance evaluation error:', err);
        this.errorMessage = 'Failed to generate evaluation. Please try again.';
      } finally {
        this.isEvaluating = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.ag-response-eval-row {
  margin-bottom: 14px;
}

.ag-response-eval-btn {
  display: inline-flex;
  align-items: center;
}

.ag-chips-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.ag-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.07);
  color: #d1d5db;
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-transform: capitalize;

  &.ag-chip-safe    { background: rgba(34, 197, 94, 0.12);  color: #86efac; border-color: rgba(34, 197, 94, 0.3); }
  &.ag-chip-warning { background: rgba(245, 158, 11, 0.12); color: #fde68a; border-color: rgba(245, 158, 11, 0.3); }
  &.ag-chip-danger  { background: rgba(239, 68, 68, 0.12);  color: #fca5a5; border-color: rgba(239, 68, 68, 0.3); }
}

.ag-card {
  background: #2b3140;
  color: #e8eaed;
  border: none;
  border-left: 4px solid #6b7280;
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.2s ease;

  &.ag-accent-danger  { border-left-color: #ef4444; }
  &.ag-accent-caution { border-left-color: #f59e0b; }
  &.ag-accent-safe    { border-left-color: #22c55e; }
  &.ag-accent-neutral { border-left-color: #6b7280; }
}

.ag-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  background: transparent;
  border: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.18s ease;

  &[aria-expanded="false"]:hover {
    background: rgba(255, 255, 255, 0.03);
    .ag-chevron { color: #f3f4f6; }
  }

  &:focus, &:focus-visible, &:active {
    outline: none;
    box-shadow: none;
  }
}

.ag-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ag-shield {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  border: 1.5px solid transparent;

  i { line-height: 1; display: inline-block; transform: translateY(2px); }

  &.ag-shield-danger  { color: #ef4444; border-color: #ef4444; background: rgba(239, 68, 68, 0.12); }
  &.ag-shield-caution { color: #f59e0b; border-color: #f59e0b; background: rgba(245, 158, 11, 0.12); }
  &.ag-shield-safe    { color: #22c55e; border-color: #22c55e; background: rgba(34, 197, 94, 0.12); }
  &.ag-shield-neutral { color: #9ca3af; border-color: #6b7280; background: rgba(156, 163, 175, 0.10); }
}

.ag-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: #f3f4f6;
}

.ag-badge {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.2px;
  border: 1px solid transparent;

  &.ag-badge-danger  { color: #fecaca; background: rgba(239, 68, 68, 0.15); border-color: rgba(239, 68, 68, 0.4); }
  &.ag-badge-caution { color: #fde68a; background: rgba(245, 158, 11, 0.15); border-color: rgba(245, 158, 11, 0.4); }
  &.ag-badge-safe    { color: #bbf7d0; background: rgba(34, 197, 94, 0.15); border-color: rgba(34, 197, 94, 0.4); }
  &.ag-badge-neutral { color: #d1d5db; background: rgba(156, 163, 175, 0.15); border-color: rgba(156, 163, 175, 0.4); }
}

.ag-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: transform 0.25s ease;
  font-size: 1.25rem;

  &.is-open { transform: rotate(180deg); }
}

.ag-body { padding: 4px 20px 20px 20px; }

.ag-timestamp {
  font-size: 0.78rem;
  color: #9ca3af;
  margin: -4px 0 14px 0;
}

.ag-summary {
  color: #d1d5db;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 12px;
}

.ag-divider {
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin: 12px 0 16px 0;
}

.ag-section {
  background: #353c4d;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
  border-left: 2px solid transparent;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:last-child { margin-bottom: 0; }
}

.ag-accent-danger .ag-section {
  border-left-color: rgba(239, 68, 68, 0.55);
  background: linear-gradient(90deg, rgba(239, 68, 68, 0.06) 0%, #353c4d 35%);
}
.ag-accent-caution .ag-section {
  border-left-color: rgba(245, 158, 11, 0.55);
  background: linear-gradient(90deg, rgba(245, 158, 11, 0.06) 0%, #353c4d 35%);
}
.ag-accent-safe .ag-section {
  border-left-color: rgba(34, 197, 94, 0.55);
  background: linear-gradient(90deg, rgba(34, 197, 94, 0.06) 0%, #353c4d 35%);
}

.ag-section-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #f3f4f6;
  margin: 0 0 6px 0;
}

.ag-section-divider {
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin: 0 0 10px 0;
}

.ag-paragraph {
  font-size: 0.88rem;
  color: #cbd0d8;
  line-height: 1.5;
  margin-bottom: 6px;

  &:last-child { margin-bottom: 0; }
}

.ag-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.ag-tag {
  font-size: 0.75rem;
  font-weight: 600;
  color: #cbd0d8;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  padding: 3px 10px;
}

.ag-loading,
.ag-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 18px 0;
  color: #cbd0d8;
  text-align: center;
}

.ag-muted { color: #9ca3af; font-size: 0.88rem; }

.ag-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.ag-btn-primary {
  background: #4c8cf5;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 8px 18px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: filter 0.15s ease;

  &:hover { filter: brightness(1.08); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:focus, &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(76, 140, 245, 0.35); }
}

.ag-btn-ghost {
  background: transparent;
  color: #d1d5db;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover { background: rgba(255, 255, 255, 0.06); color: #f3f4f6; }
  &:focus, &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}
</style>